import { useCallback, useState } from "react";

import { useGetOtp } from "../../../hooks/useGetOtp";
import { useResetOtp } from "../../../hooks/useResetOtp";
import { useUnblockUser } from "../../../hooks/useUnblockUser";
import { useUpdatePassword } from "../../../hooks/useUpdatePassword";
import { getOtpCodeFromResponse } from "../utils/studentDetailsHelpers";
import { validatePassword } from "../utils/passwordValidation";
import useSnackbarState from "./useSnackbarState";

const useOtpPasswordManagementActions = ({
  phone,
  studentData,
  verticalInfo,
  branchId,
}) => {
  const [otpModal, setOtpModal] = useState({ open: false, code: "" });
  const [passwordModal, setPasswordModal] = useState({
    open: false,
    password: "",
    error: "",
  });
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    actionText: "",
  });
  const {
    errorSnackbar,
    successSnackbar,
    showError,
    showSuccess,
    closeError,
    closeSuccess,
  } = useSnackbarState();

  const {
    mutate: getOtp,
    isPending: isGetOtpPending,
    isLoading: isGetOtpLoading,
  } = useGetOtp();
  const {
    mutate: updatePassword,
    isPending: isUpdatePasswordPending,
    isLoading: isUpdatePasswordLoading,
  } = useUpdatePassword();
  const {
    mutate: resetOtp,
    isPending: isResetOtpPending,
    isLoading: isResetOtpLoading,
  } = useResetOtp();
  const {
    mutate: unblockUser,
    isPending: isUnblockUserPending,
    isLoading: isUnblockUserLoading,
  } = useUnblockUser();

  const isUpdatingPassword = isUpdatePasswordPending || isUpdatePasswordLoading;
  const isGettingOtp = isGetOtpPending || isGetOtpLoading;
  const isResettingOtp = isResetOtpPending || isResetOtpLoading;
  const isUnblockingUser = isUnblockUserPending || isUnblockUserLoading;

  const closeOtpModal = useCallback(() => {
    setOtpModal({ open: false, code: "" });
  }, []);

  const closePasswordModal = useCallback(() => {
    setPasswordModal({ open: false, password: "", error: "" });
  }, []);

  const closeConfirmModal = useCallback(() => {
    setConfirmModal({ open: false, actionText: "" });
  }, []);

  const openPasswordModal = useCallback(() => {
    setPasswordModal({ open: true, password: "", error: "" });
  }, []);

  const updatePasswordValue = useCallback((password) => {
    setPasswordModal((prev) => ({
      ...prev,
      password,
      error: validatePassword(password),
    }));
  }, []);

  const handleUpdatePassword = useCallback(() => {
    const err = validatePassword(passwordModal.password);
    if (err) {
      setPasswordModal((prev) => ({ ...prev, error: err }));
      return;
    }

    updatePassword(
      {
        admission_id: studentData?.admission_number,
        password: passwordModal.password,
        branch: branchId,
        product_id: verticalInfo?.productId,
        tenant_id: verticalInfo?.tenantId,
      },
      {
        onSuccess: () => {
          showSuccess("Password updated successfully.");
          closePasswordModal();
        },
        onError: (err) => {
          showError(err.message || "Failed to update password.");
        },
      },
    );
  }, [
    branchId,
    closePasswordModal,
    passwordModal.password,
    showError,
    showSuccess,
    studentData?.admission_number,
    updatePassword,
    verticalInfo?.productId,
    verticalInfo?.tenantId,
  ]);

  const handleGetOtp = useCallback(() => {
    getOtp(
      {
        verticalName: verticalInfo?.name,
        phone,
        isd_code: "+91",
        tenant_id: verticalInfo?.tenantId || "1",
        product_id: verticalInfo?.productId || "300",
      },
      {
        onSuccess: (res) => {
          const code = getOtpCodeFromResponse(res);
          if (!code) {
            showError("OTP not found.");
            return;
          }

          setOtpModal({ open: true, code });
          showSuccess("OTP fetched successfully.");
        },
        onError: (err) => {
          showError(err.message || "Failed to fetch OTP.");
        },
      },
    );
  }, [
    getOtp,
    phone,
    showError,
    showSuccess,
    verticalInfo?.name,
    verticalInfo?.productId,
    verticalInfo?.tenantId,
  ]);

  const handleConfirmYes = useCallback(() => {
    const action = confirmModal.actionText;
    closeConfirmModal();

    if (action === "Reset OTP") {
      resetOtp(
        { phone, isd_code: "+91" },
        {
          onSuccess: () => showSuccess("OTP reset successfully."),
          onError: (err) => showError(err.message || "Failed to reset OTP."),
        },
      );
      return;
    }

    if (action === "Unblock User") {
      unblockUser(
        {
          admission_id: studentData?.admission_number,
          productId: verticalInfo?.productId,
        },
        {
          onSuccess: () => showSuccess("User unblocked successfully."),
          onError: (err) => showError(err.message || "Failed to unblock user."),
        },
      );
    }
  }, [
    closeConfirmModal,
    confirmModal.actionText,
    phone,
    resetOtp,
    showError,
    showSuccess,
    studentData?.admission_number,
    unblockUser,
    verticalInfo?.productId,
  ]);

  return {
    otpModal,
    passwordModal,
    confirmModal,
    errorSnackbar,
    successSnackbar,
    setConfirmModal,
    closeOtpModal,
    closePasswordModal,
    closeConfirmModal,
    openPasswordModal,
    updatePasswordValue,
    closeError,
    closeSuccess,
    isUpdatingPassword,
    isGettingOtp,
    isResettingOtp,
    isUnblockingUser,
    handleUpdatePassword,
    handleGetOtp,
    handleConfirmYes,
  };
};

export default useOtpPasswordManagementActions;
